import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import ReactMarkdown from "react-markdown";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../components/ui/collapsible";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "../components/ui/resizable";
import { toast } from "sonner";
import API from "../lib/api";
import {
  Plus, Search, ArrowRight, Pencil, Eye, Layers,
  CheckCircle2, AlertTriangle, FileCheck, EyeOff,
  ChevronRight, ChevronDown, FileText, Sparkles, RefreshCw, Store,
  ShieldCheck, XCircle, HelpCircle, MapPin, Bot, Database, Hash,
  ExternalLink, ListFilter, X
} from "lucide-react";
import ConsolidatedFindingsBoard from "../components/ConsolidatedFindingsBoard";
import { PageLayout } from "../components/PageLayout";
import EvidencePdfFrame from "../components/EvidencePdfFrame";
import { buildSourceIndex, parseWhereToLook } from "../lib/sourceFiles";

const PREVIEW_REFERENCE_LIMIT = 4;
const MODAL_ANOMALY_PREVIEW_LIMIT = 10;
const REVIEW_STATES = {
  accepted: "accepted",
  rejected: "rejected",
  needs_evidence: "needs_evidence",
};
// const RELIANCE_FINDING_SPINE = [
//   "SBL-MP-014",
//   "SUP-1187",
//   "SUP-1188",
//   "EID-7842",
//   "EID-0417",
//   "IND-AT-2025-1142",
// ];

const EMPTY_FINDING_FORM = {
  control_id: "",
  category: "operational",
  suggested_severity: "Medium",
  finding_title: "",
  finding_description: "",
  evidence_summary: "",
  business_impact: "",
  root_cause: "",
  remediation_plan: "",
  owner_response: "",
  affected_references: [],
  affected_references_text: ""
};

// const RELIANCE_DUPLICATE_CONTROL_GROUPS = [["REGULA-094", "REGULA-095"]];

function verdictMeta(verdict) {
  switch ((verdict || "").toUpperCase()) {
    case "PASS": return { label: "PASS", Icon: ShieldCheck, accent: "border-l-emerald-500", badgeCls: "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200" };
    case "FAIL": return { label: "FAIL", Icon: XCircle, accent: "border-l-red-500", badgeCls: "text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border-red-200" };
    case "EXCEPTION": return { label: "EXCEPTION", Icon: AlertTriangle, accent: "border-l-amber-500", badgeCls: "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border-amber-200" };
    default: return { label: "INCONCLUSIVE", Icon: HelpCircle, accent: "border-l-slate-400", badgeCls: "text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/40 border-slate-200" };
  }
}

const ExpandableBadgeList = ({ items, limit = PREVIEW_REFERENCE_LIMIT, className = "" }) => {
  const normalized = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!normalized.length) return null;

  return (
    <div className={`flex flex-wrap gap-2 ${className}`.trim()}>
      {normalized.slice(0, limit).map((item, index) => (
        <Badge key={`${String(item)}-${index}`} variant="outline" className="max-w-full truncate font-mono text-[10px]">
          {item}
        </Badge>
      ))}
      {normalized.length > limit && (
        <Badge variant="outline" className="text-[10px]">
          +{normalized.length - limit} more
        </Badge>
      )}
    </div>
  );
};

const buildReferenceLabel = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item.trim();

  const row = item.row_data || item.raw || item;
  const filename =
    row?.filename ||
    row?.source_file ||
    row?.ocr_filename ||
    row?.artifact_filename ||
    item?.filename ||
    "";
  const ids = [
    row?.invoice_id,
    row?.supplier_id,
    row?.employee_id,
    row?.store_id,
    row?.complaint_id,
    row?.cctv_event_id,
    row?.register_id,
  ].filter(Boolean);

  const label = [filename, ...ids].filter(Boolean).join(" - ").trim();
  return label || String(item.evidence_id || item.source_table || "").trim();
};

const buildAnomalyPreview = (item) => {
  const row = item?.row_data || item?.raw || item;
  if (!row || typeof row !== "object") return String(item?.preview || item?.raw || "").trim();

  const preferred = [
    row.source_excerpt,
    row.sample_description,
    row.raw_text,
    row.Entry,
    row.entry,
    row["Item Description"],
  ].find((value) => String(value || "").trim());
  if (preferred) return String(preferred).trim();

  const pairs = Object.entries(row)
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim())
    .slice(0, 4)
    .map(([key, value]) => `${key}: ${value}`);
  return pairs.join(" | ");
};

const SidebarGroup = ({ label, children }) => (
  <div className="space-y-0.5">
    <p className="px-2 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
      {label}
    </p>
    <div className="space-y-1 pt-1">{children}</div>
  </div>
);

const SidebarRow = ({ label, count, active, onClick, dot }) => (
  <button
    onClick={onClick}
    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
      active
        ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/[0.04]"
    }`}
  >
    <span className="flex items-center gap-2">
      {dot && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />}
      <span>{label}</span>
    </span>
    <span className="rounded-md bg-slate-200/70 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-500 dark:bg-white/[0.06] dark:text-slate-400">
      {count}
    </span>
  </button>
);

const EVIDENCE_TONES = {
  red: {
    dot: "bg-red-500",
    border: "border-red-200/70 dark:border-red-500/20",
    bg: "bg-red-50/60 dark:bg-red-500/[0.03]",
    text: "text-red-800 dark:text-red-300",
    label: "FAILING TEST RESULT",
  },
  green: {
    dot: "bg-emerald-500",
    border: "border-emerald-200/70 dark:border-emerald-500/20",
    bg: "bg-emerald-50/60 dark:bg-emerald-500/[0.03]",
    text: "text-emerald-900 dark:text-emerald-50/80",
    label: "SUPPORTING POLICY",
  },
  orange: {
    dot: "bg-amber-500",
    border: "border-amber-200/70 dark:border-amber-500/20",
    bg: "bg-amber-50/60 dark:bg-amber-500/[0.03]",
    text: "text-amber-900 dark:text-amber-300",
    label: "OVERRIDE DECISION",
  },
  neutral: {
    dot: "bg-indigo-500",
    border: "border-slate-200 dark:border-white/[0.06]",
    bg: "bg-slate-50 dark:bg-white/[0.02]",
    text: "text-slate-700 dark:text-slate-300",
    label: "SOURCE",
  },
};

const KeyEvidenceRow = ({ ev, onPreview }) => {
  const [open, setOpen] = useState(false);
  const tone = EVIDENCE_TONES[ev.tone] || EVIDENCE_TONES.neutral;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={`rounded-xl border ${tone.border} ${tone.bg} overflow-hidden`}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <CollapsibleTrigger asChild>
          <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left">
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${tone.dot}`} />
            <span className="truncate font-mono text-[11px] font-semibold text-slate-700 dark:text-slate-300">{ev.entity || ev.filename}</span>
            {ev.filename && ev.entity ? (
              <span className="hidden shrink-0 truncate text-[10px] text-slate-400 dark:text-slate-500 md:inline">{ev.filename}</span>
            ) : null}
            {ev.page ? (
              <span className="shrink-0 rounded-md border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 font-mono text-[9px] font-bold text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300">
                p.{ev.page}
              </span>
            ) : null}
            {ev.section ? (
              <span className="shrink-0 rounded-md border border-violet-200 bg-violet-50 px-1.5 py-0.5 font-mono text-[9px] font-bold text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300">
                §{ev.section}
              </span>
            ) : null}
            <span className="hidden shrink-0 text-[8px] font-black uppercase tracking-wider text-slate-400 sm:inline">{tone.label}</span>
            {ev.snippet ? (
              <ChevronDown className={`ml-auto h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
            ) : null}
          </button>
        </CollapsibleTrigger>
        {ev.doc && (
          <button
            type="button"
            onClick={() => onPreview?.(ev)}
            title={`Preview ${ev.filename}`}
            className="shrink-0 h-6 w-6 rounded-lg flex items-center justify-center text-slate-400 hover:text-emerald-600 hover:bg-emerald-500/10 transition-colors"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {ev.snippet ? (
        <CollapsibleContent>
          <div className={`border-t px-3 py-2.5 ${tone.border}`}>
            <p className={`text-xs leading-relaxed whitespace-pre-wrap ${tone.text}`}>{ev.snippet}</p>
          </div>
        </CollapsibleContent>
      ) : null}
    </Collapsible>
  );
};

export default function Phase8Findings() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, permissions, findings = [], fetchFindings } = useAudit();
  const [teamData, setTeamData] = useState(null);

  const [detailsView, setDetailsView] = useState(null);
  const [traceView, setTraceView] = useState(null);
  const [editFinding, setEditFinding] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newFinding, setNewFinding] = useState(EMPTY_FINDING_FORM);
  const [editTeamFinding, setEditTeamFinding] = useState(null);
  const [findingReviews, setFindingReviews] = useState({});

  const [selectedVerdict, setSelectedVerdict] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSeverity, setSelectedSeverity] = useState("ALL");
  const [selectedReviewStatus, setSelectedReviewStatus] = useState("ALL");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [selectedControl, setSelectedControl] = useState("ALL");

  const canEditFindings = permissions?.includes("findings:edit") ?? true;
  const canSuppressFindings = permissions?.includes("findings:suppress") ?? true;
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewPage, setPreviewPage] = useState(null);
  const [previewSearch, setPreviewSearch] = useState("");
  const [paneUrl, setPaneUrl] = useState(null);
  const [groupBy, setGroupBy] = useState("none");

  const [sourceIndex, setSourceIndex] = useState(null);

  const isTeamMode = auditRun?.audit_mode === "team";
  const auditType = String(auditRun?.audit_type || auditRun?.context?.audit_type || "").toUpperCase();
  const auditDomain = String(auditRun?.context?.audit_domain || "").toLowerCase();
  const auditName = String(auditRun?.name || "").toUpperCase();
  // const isRelianceRetailDemo =
  //   auditDomain === "retail" &&
  //   (
  //     auditName.includes("RELIANCE") ||
  //     auditName.includes("SMART BAZAAR") ||
  //     auditType.includes("SHRINK") ||
  //     auditType.includes("LOSS PREVENTION") ||
  //     auditType.includes("PROCUREMENT") ||
  //     auditType.includes("STORE OPERATIONS")
  //   );
  const hasTeamFindings = isTeamMode && teamData?.consolidated_findings?.length > 0;
  const fieldValue = (v, fallback) => (v && String(v).trim() ? v : fallback);

  const fetchTeamData = useCallback(async () => {
    if (!auditRun?.id || !isTeamMode) return;
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/team`);
      setTeamData(data);
    } catch (e) { console.error("Failed to fetch team data", e); }
  }, [auditRun?.id, isTeamMode]);



  useEffect(() => {
    if (findings.length === 0) fetchFindings();
    fetchTeamData();
  }, [findings.length, fetchFindings, fetchTeamData]);

  useEffect(() => {
    if (!auditRun?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [docsRes, artifactsRes] = await Promise.all([
          API.get(`/audit-runs/${auditRun.id}/documents?limit=500`).catch(() => ({ data: [] })),
          API.get(`/audit-runs/${auditRun.id}/artifacts?limit=500`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const docs = Array.isArray(docsRes.data) ? docsRes.data : [];
        const artifacts =
          artifactsRes.data && Array.isArray(artifactsRes.data.artifacts)
            ? artifactsRes.data.artifacts
            : Array.isArray(artifactsRes.data)
              ? artifactsRes.data
              : [];
        setSourceIndex(buildSourceIndex(docs, artifacts));
      } catch (e) {
        console.error("Failed to build source file index", e);
        if (!cancelled) setSourceIndex(null);
      }
    })();
    return () => { cancelled = true; };
  }, [auditRun?.id]);

  const handleSuppress = async (id) => {
    try {
      await API.post(`/audit-runs/${auditRun.id}/findings/${id}/suppress`);
      toast.success("Finding suppressed");
      fetchFindings();
    } catch (e) { toast.error("Failed to suppress"); }
  };

  const handleEdit = async () => {
    try {
      const payload = { ...editFinding };
      if (typeof payload.affected_references_text === "string") {
        const parts = payload.affected_references_text.split(",").map((s) => s.trim()).filter(Boolean);
        if (parts.length) payload.affected_references = parts;
        delete payload.affected_references_text;
      }
      await API.put(`/audit-runs/${auditRun.id}/findings/${payload.id}`, payload);
      setEditFinding(null);
      toast.success("Finding updated");
      fetchFindings();
    } catch (e) { toast.error("Failed to update"); }
  };

  const handleAdd = async () => {
    try {
      const payload = { ...newFinding };
      if (typeof payload.affected_references_text === "string" && payload.affected_references_text.trim()) {
        payload.affected_references = payload.affected_references_text.split(",").map((s) => s.trim()).filter(Boolean);
      }
      delete payload.affected_references_text;
      if (!payload.finding_title?.trim() || !payload.control_id?.trim()) {
        toast.error("Control ID and Title are required");
        return;
      }
      await API.post(`/audit-runs/${auditRun.id}/findings`, payload);
      setAddOpen(false);
      setNewFinding(EMPTY_FINDING_FORM);
      toast.success("Finding added");
      fetchFindings();
    } catch (e) { toast.error("Failed to add"); }
  };

  const handleContinue = async () => {
    await advancePhase(9);
    navigate("/phase/9");
  };

  const sevClass = (s) => ({
    Critical: "text-red-700 border-red-300 bg-red-50 dark:bg-red-950/40 dark:border-red-800",
    High: "text-red-600 border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800",
    Medium: "text-amber-600 border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800",
    Low: "text-blue-600 border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800",
    Observation: "text-slate-600 border-slate-200 bg-slate-50 dark:bg-slate-950/30 dark:border-slate-800",
  }[s] || "");

  const sanitizeFindingTitle = (t) => {
    let s = String(t || "");
    // Strip internal pipeline prefixes that are meaningless to auditors
    const TECH_PREFIXES = [
      /^SQL Policy Validation:\s*/i,
      /^SQL Validation:\s*/i,
      /^Policy Validation:\s*/i,
      /^Policy Check:\s*/i,
      /^Control Check:\s*/i,
      /^RAG Validation:\s*/i,
      /^RAG Check:\s*/i,
      /^Control Validation:\s*/i,
      /^Audit Control:\s*/i,
      /^Rule Check:\s*/i,
    ];
    for (const re of TECH_PREFIXES) {
      s = s.replace(re, "");
    }
    // Replace underscores, trim stray colons/dashes at start
    return s.replace(/_/g, " ").replace(/^[:\-â€“\s]+/, "").trim() || t;
  };
  const sanitizeEvidenceSummary = (raw) => {
    if (!raw) return "";
    try {
      const p = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (p.explanation) return p.explanation;
    } catch (e) { }
    return String(raw);
  };

  const getAnomalyEntries = (f) => {
    if (!f?.anomalous_items || !Array.isArray(f.anomalous_items)) return [];
    return f.anomalous_items.map((item, index) => {
      if (typeof item === "string") {
        return {
          id: `anomaly-${index}`,
          title: `Anomaly ${index + 1}`,
          preview: item,
          raw: item,
          reference: item,
        };
      }
      return {
        id: item.id || item.evidence_id || `anomaly-${index}`,
        title: item.title || `Anomaly ${index + 1}`,
        preview: buildAnomalyPreview(item),
        raw: item,
        reference: buildReferenceLabel(item),
      };
    });
  };

  const getEvidenceReferences = (finding) => {
    const anomalyRefs = getAnomalyEntries(finding)
      .map((item) => item.reference)
      .filter(Boolean);
    const explicitRefs = Array.isArray(finding?.evidence_refs) ? finding.evidence_refs.filter(Boolean) : [];
    const affectedRefs = Array.isArray(finding?.affected_references) ? finding.affected_references.filter(Boolean) : [];
    return Array.from(new Set([...anomalyRefs, ...affectedRefs, ...explicitRefs]));
  };

  const getFindingCardCount = (finding) => {
    const refs = getEvidenceReferences(finding);
    return refs.length;
  };

  const getRowData = (entry) => {
    const raw = entry?.raw;
    if (!raw || typeof raw !== "object") return null;
    return raw.row_data || raw.raw || raw;
  };

  const toneForSource = (source) => {
    const s = String(source || "").toLowerCase();
    if (/override|recommend|decision|write-?off|management/.test(s)) return "orange";
    if (/fail|error|exception|non-?compliant|deficien|shortfall/.test(s)) return "red";
    return "green";
  };

  const buildKeyEvidence = (finding) => {
    if (!finding) return [];
    const entries = [];
    const seen = new Set();
    const push = (entry) => {
      if (!entry?.filename && !entry?.snippet) return;
      const key = `${entry.filename || ""}::${entry.page || ""}::${String(entry.snippet || "").slice(0, 40)}`;
      if (seen.has(key)) return;
      seen.add(key);
      entries.push(entry);
    };

    getAnomalyEntries(finding).forEach((item) => {
      const row = getRowData(item);
      const raw = item?.raw;
      const filename = row?.filename || row?.source_file || row?.artifact_filename || raw?.source_file || "";
      const page = row?.page_number || row?.page || raw?.page_number || "";
      const snippet = item.preview || "";
      const doc = filename ? sourceIndex?.resolve(filename) : null;
      push({
        id: item.id || `anomaly-${entries.length}`,
        filename: doc?.filename || filename || "Anomalous record",
        page,
        section: "",
        snippet,
        search: snippet,
        tone: "red",
        doc: doc || null,
      });
    });

    (Array.isArray(finding.where_to_look) ? finding.where_to_look : []).forEach((source) => {
      const parsed = parseWhereToLook(source);
      const doc = sourceIndex?.resolve(parsed.filename || source);
      push({
        id: `where-${entries.length}`,
        filename: doc?.filename || parsed.filename || source,
        entity: parsed.entity,
        snippet: parsed.entity ? parsed.entity : "",
        page: parsed.page || "",
        section: parsed.section || "",
        search: parsed.entity || "",
        tone: toneForSource(source),
        doc: doc || null,
      });
    });

    return entries;
  };

  const severityTone = (finding) => {
    const sev = String(finding?.final_severity || finding?.suggested_severity || finding?.severity || "").toLowerCase();
    if (sev === "critical") return { bar: "bg-red-600", chip: "bg-red-50 text-red-700 border-red-300 dark:bg-red-950/50 dark:text-red-300 dark:border-red-800", dot: "bg-red-600" };
    if (sev === "high") return { bar: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800", dot: "bg-red-500" };
    if (sev === "medium") return { bar: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800", dot: "bg-amber-500" };
    if (sev === "low") return { bar: "bg-sky-500", chip: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800", dot: "bg-sky-500" };
    return { bar: "bg-slate-300 dark:bg-slate-700", chip: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-900/40 dark:text-slate-400 dark:border-slate-800", dot: "bg-slate-400" };
  };

  const anomalyRatio = (finding) => {
    const count = getAnomalyCount(finding);
    const total = Number(finding?.total_entities || finding?.total || 0);
    if (count > 0 && total > 0) return Math.min(100, Math.round((count / total) * 100));
    return null;
  };

  // const getMergedDuplicateNote = (finding) => {
  //   if (!isRelianceRetailDemo) return "";
  //   const controlId = String(finding?.control_id || "");
  //   if (controlId !== "REGULA-094") return "";
  //   const hasSuppressedPair = findings.some(
  //     (item) =>
  //       !item.suppressed &&
  //       String(item.control_id || "") === "REGULA-095" &&
  //       getAnomalyCount(item) === getAnomalyCount(finding)
  //   );
  //   return hasSuppressedPair ? "Combined with REGULA-095 because both clauses point to the same invoice-description defect." : "";
  // };

  const evidenceOf = (f) => {
    const refs = getEvidenceReferences(f);
    if (refs.length) return `${refs.length} reference${refs.length > 1 ? "s" : ""}`;
    return f?.evidence_narrative ? "Narrative available" : "â€”";
  };

  const findingCardDelay = (index) => ({ "--finding-delay": `${index * 60}ms` });

  const activeFindings = useMemo(() => {
    let base = findings.filter((f) => !f.suppressed);


    // Reliance deduping
    // if (isRelianceRetailDemo) {
    //   const anomalyPreviewSignature = (finding) =>
    //     (Array.isArray(finding?.anomalous_items) ? finding.anomalous_items : [])
    //       .map((item, index) => {
    //         if (typeof item === "string") return item;
    //         return buildAnomalyPreview({
    //           ...item,
    //           id: item?.id || item?.evidence_id || `anomaly-${index}`,
    //         });
    //       })
    //       .join("||");
    //   const anomalyCountForFinding = (finding) =>
    //     Array.isArray(finding?.anomalous_items) ? finding.anomalous_items.length : 0;

    //   const deduped = [...base];
    //   for (const group of RELIANCE_DUPLICATE_CONTROL_GROUPS) {
    //     const matches = deduped.filter((finding) => group.includes(String(finding.control_id || "")));
    //     if (matches.length < 2) continue;

    //     matches.sort((a, b) => String(a.control_id || "").localeCompare(String(b.control_id || "")));
    //     const keeper = matches[0];
    //     const keeperAnomalies = anomalyPreviewSignature(keeper);

    //     for (const duplicate of matches.slice(1)) {
    //       const duplicateAnomalies = anomalyPreviewSignature(duplicate);
    //       if (
    //         anomalyCountForFinding(duplicate) === anomalyCountForFinding(keeper) &&
    //         duplicateAnomalies === keeperAnomalies
    //       ) {
    //         const index = deduped.findIndex((item) => item.id === duplicate.id);
    //         if (index >= 0) deduped.splice(index, 1);
    //       }
    //     }
    //   }
    //   base = deduped;
    // }

    // Search Filter
    if (searchTerm) {
      const lowSearch = searchTerm.toLowerCase();
      base = base.filter(f =>
        (f.title || f.finding_title || "").toLowerCase().includes(lowSearch) ||
        (f.control_id || f.control_ref || "").toLowerCase().includes(lowSearch)
      );
    }

    // Verdict Filter
    if (selectedVerdict !== "ALL") {
      base = base.filter(f => (f.verdict || "INCONCLUSIVE").toUpperCase() === selectedVerdict);
    }

    // Severity Filter
    if (selectedSeverity !== "ALL") {
      base = base.filter(f => (f.final_severity || f.suggested_severity || f.severity || "").toLowerCase() === selectedSeverity.toLowerCase());
    }

    // Review Status Filter
    if (selectedReviewStatus !== "ALL") {
      base = base.filter(f => {
        const review = findingReviews[f.id];
        const status = review?.status || f.review_status;
        const isReviewed = status === 'accepted' || status === 'rejected';
        return selectedReviewStatus === "Reviewed" ? isReviewed : !isReviewed;
      });
    }

    // Group Filter
    if (selectedGroup !== "all") {
      base = base.filter(f => {
        const review = findingReviews[f.id];
        const status = review?.status || f.review_status || "";
        switch (selectedGroup) {
          case "needs_review": return !["accepted", "rejected"].includes(status);
          case "open": return status === "accepted";
          case "resolved": return status === "rejected";
          case "accepted_risk": return status === "needs_evidence";
          default: return true;
        }
      });
    }

    // Control Filter
    if (selectedControl !== "ALL") {
      base = base.filter(f => (f.control_ref || f.control_id || "") === selectedControl);
    }

    return base;
  }, [findings,  selectedVerdict, searchTerm, selectedSeverity, selectedReviewStatus, selectedGroup, selectedControl, findingReviews]);

  const groupedFindings = useMemo(() => {
    if (groupBy === "none") {
      return [{ label: null, items: activeFindings }];
    }
    const order = [];
    const groups = new Map();
    activeFindings.forEach((f) => {
      let key = "Unmapped";
      if (groupBy === "control") key = String(f.control_ref || f.control_id || "Unmapped").split("-")[0] || "Unmapped";
      else if (groupBy === "severity") key = f.final_severity || f.suggested_severity || f.severity || "Unassigned";
      else if (groupBy === "verdict") key = f.verdict || "INCONCLUSIVE";
      if (!groups.has(key)) {
        groups.set(key, []);
        order.push(key);
      }
      groups.get(key).push(f);
    });
    const severityOrder = { Critical: 0, High: 1, Medium: 2, Low: 3, Observation: 4 };
    order.sort((a, b) => {
      if (groupBy === "severity") return (severityOrder[a] ?? 99) - (severityOrder[b] ?? 99);
      return String(a).localeCompare(String(b));
    });
    return order.map((label) => ({ label, items: groups.get(label) }));
  }, [activeFindings, groupBy]);

  const reviewedFindingsCount = useMemo(
    () =>
      activeFindings.filter((finding) => {
        const status = findingReviews[finding.id]?.status || finding.review_status || REVIEW_STATES.accepted;
        return Object.values(REVIEW_STATES).includes(status);
      }).length,
    [activeFindings, findingReviews]
  );
  const canAdvanceToClassification = findings.filter(f => !f.suppressed).length > 0 || hasTeamFindings;

  const formatAnomalySummary = (item) => item.preview || item.raw || "No detail";
  const getAnomalyCount = (finding) => getAnomalyEntries(finding).length;

  const parseEvidencePayload = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const auditorExplanation = (finding) => {
    // Support both new backend fields and legacy fields gracefully
    const narrative = finding?.evidence_narrative || finding?.summary || "";
    const payload = parseEvidencePayload(finding?.evidence_summary);
    const anomalyCount = getAnomalyCount(finding);
    return {
      whyFlagged: fieldValue(
        payload?.why_flagged || finding?.finding_description || finding?.description,
        "This item was flagged because the extracted evidence suggests a control deviation that should be reviewed."
      ),
      evidenceBasis: fieldValue(
        payload?.evidence_basis || payload?.explanation || narrative || sanitizeEvidenceSummary(finding?.evidence_summary),
        "Evidence was drawn from the supporting records linked to this finding."
      ),
      likelyCause: fieldValue(
        payload?.likely_cause || finding?.root_cause,
        anomalyCount > 0
          ? `The pattern appears to be driven by ${anomalyCount} anomalous record${anomalyCount === 1 ? "" : "s"} that warrant auditor review.`
          : "The underlying cause has not been documented yet."
      ),
      nextStep: fieldValue(
        payload?.next_step || finding?.remediation_plan,
        "Validate the cited evidence, confirm the impact, and finalize the remediation path."
      ),
    };
  };

  const businessImpactValue = (finding) =>
    fieldValue(finding?.business_impact, "Business impact has not been documented yet.");
  const rootCauseValue = (finding) =>
    fieldValue(finding?.root_cause, "Root cause has not been documented yet.");
  const remediationPlanValue = (finding) =>
    fieldValue(finding?.remediation_plan, "Remediation plan has not been documented yet.");
  const managementResponseValue = (finding) =>
    fieldValue(finding?.owner_response || finding?.management_response, "Management response has not been captured yet.");

  const closeSheet = () => {
    setSelectedFinding(null);
    setPreviewDoc(null);
    setPreviewPage(null);
    setPreviewSearch("");
    setPaneUrl(null);
  };

  const handleEvidencePreview = (ev) => {
    if (!ev?.doc) return;
    setPaneUrl(null);
    setPreviewDoc(ev.doc);
    setPreviewPage(ev.page || null);
    setPreviewSearch(ev.search || "");
  };

  const keyEvidence = useMemo(
    () => buildKeyEvidence(selectedFinding),
    [selectedFinding, sourceIndex]
  );

  const actionFields = useMemo(() => {
    const f = selectedFinding;
    if (!f) return [];
    const fields = [];
    if (f.remediation_plan) fields.push({ label: "Recommended Remediation", value: f.remediation_plan, Icon: CheckCircle2 });
    if (f.owner_response || f.management_response) fields.push({ label: "Owner / Response", value: f.owner_response || f.management_response, Icon: Bot });
    if (f.business_impact) fields.push({ label: "Business Impact", value: f.business_impact, Icon: AlertTriangle });
    return fields;
  }, [selectedFinding]);

  const updateFindingReview = (id, patch) => {
    setFindingReviews((current) => ({
      ...current,
      [id]: {
        status: current[id]?.status || REVIEW_STATES.accepted,
        note: current[id]?.note || "",
        ...patch,
      },
    }));
  };

  const stats = useMemo(() => {
    const unsuppressed = findings.filter(f => !f.suppressed);
    const severityOf = (f) => (f.final_severity || f.suggested_severity || f.severity || "").toLowerCase();
    const reviewStatusOf = (f) => findingReviews[f.id]?.status || f.review_status || "";
    return {
      total: unsuppressed.length,
      fail: unsuppressed.filter(f => (f.verdict || "").toUpperCase() === "FAIL").length,
      pass: unsuppressed.filter(f => (f.verdict || "").toUpperCase() === "PASS").length,
      exception: unsuppressed.filter(f => (f.verdict || "").toUpperCase() === "EXCEPTION").length,
      inconclusive: unsuppressed.filter(f => ["INCONCLUSIVE", ""].includes((f.verdict || "").toUpperCase())).length,
      critical: unsuppressed.filter(f => severityOf(f) === "critical").length,
      high: unsuppressed.filter(f => severityOf(f) === "high").length,
      medium: unsuppressed.filter(f => severityOf(f) === "medium").length,
      low: unsuppressed.filter(f => severityOf(f) === "low").length,
      observation: unsuppressed.filter(f => severityOf(f) === "observation").length,
      unresolved: unsuppressed.filter(f => !["accepted", "rejected"].includes(reviewStatusOf(f))).length,
    };
  }, [findings, findingReviews]);

  const controlOptions = useMemo(() => {
    const set = new Set();
    findings.filter(f => !f.suppressed).forEach(f => {
      const ref = f.control_ref || f.control_id;
      if (ref) set.add(ref);
    });
    return Array.from(set).sort();
  }, [findings]);

  const reviewStatusOf = (finding) =>
    String(findingReviews[finding.id]?.status || finding.review_status || "").toLowerCase();

  const SEVERITY_LEVELS = [
    { key: "Critical", label: "Critical", dot: "bg-red-600", count: stats.critical },
    { key: "High", label: "High", dot: "bg-orange-500", count: stats.high },
    { key: "Medium", label: "Medium", dot: "bg-amber-500", count: stats.medium },
    { key: "Low", label: "Low", dot: "bg-sky-500", count: stats.low },
    { key: "Observation", label: "Observation", dot: "bg-slate-400", count: stats.observation },
  ];

  const VERDICT_LEVELS = [
    { key: "FAIL", label: "Fail", dot: "bg-rose-500", count: stats.fail },
    { key: "EXCEPTION", label: "Exception", dot: "bg-amber-500", count: stats.exception },
    { key: "INCONCLUSIVE", label: "Inconclusive", dot: "bg-slate-400", count: stats.inconclusive },
    { key: "PASS", label: "Pass", dot: "bg-emerald-500", count: stats.pass },
  ];

  const needsReviewCount = useMemo(
    () => findings.filter((f) => !f.suppressed && !reviewStatusOf(f)).length,
    [findings, findingReviews]
  );

  const handleReviewUnresolved = () => {
    const first = activeFindings.find((f) => !reviewStatusOf(f));
    if (first) setSelectedFinding(first);
    else toast.info("No unresolved findings.");
  };

  const statRow = [
    { label: "Total", value: stats.total, accent: "text-slate-900 dark:text-white", bg: "bg-slate-50 dark:bg-white/[0.02]" },
    { label: "Critical", value: stats.critical, accent: "text-red-600", bg: "bg-slate-50/60 dark:bg-white/[0.01]" },
    { label: "High", value: stats.high, accent: "text-orange-500", bg: "bg-slate-50/60 dark:bg-white/[0.01]" },
    { label: "Medium", value: stats.medium, accent: "text-amber-500", bg: "bg-slate-50/60 dark:bg-white/[0.01]" },
    { label: "Low", value: stats.low, accent: "text-sky-500", bg: "bg-slate-50/60 dark:bg-white/[0.01]" },
    { label: "Observation", value: stats.observation, accent: "text-slate-500", bg: "bg-slate-50/60 dark:bg-white/[0.01]" },
  ];

  return (
    <PageLayout className="bg-slate-50 dark:bg-[#0b0d11] text-slate-900 dark:text-slate-100" data-testid="phase-8">
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 dark:border-white/[0.06] bg-white dark:bg-[#12141a] shadow-sm">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 dark:border-white/[0.06] bg-white dark:bg-[#15171c] px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">Phase 8  Findings</h2>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              Review, validate and finalize findings generated during control testing.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setAddOpen(true)} className="h-9 gap-1.5 rounded-lg border-slate-200 text-xs font-semibold">
              <Plus className="h-3.5 w-3.5" /> New Finding
            </Button>
            <Button variant="ghost" onClick={fetchFindings} className="h-9 gap-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-900 dark:text-slate-300" data-testid="refresh-findings-btn">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!canAdvanceToClassification}
              className="h-9 gap-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-xs font-bold text-white shadow-md shadow-emerald-600/30 transition-all active:scale-95 border-0"
              data-testid="continue-to-classification-btn"
            >
              Continue to Classification <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 divide-x divide-slate-100 dark:divide-white/[0.04] sm:grid-cols-6">
          {statRow.map((stat) => (
            <div key={stat.label} className={`flex flex-col items-center justify-center gap-0.5 px-3 py-4 first:rounded-l-xl last:rounded-r-xl ${stat.bg}`}>
              <span className={`text-3xl font-black ${stat.accent}`}>{stat.value}</span>
              <span className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 dark:border-white/[0.06] bg-slate-50/70 dark:bg-white/[0.01] px-4 py-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              placeholder="Search findings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-9 rounded-xl border-slate-200 pl-10 text-xs dark:border-white/[0.08] dark:bg-white/[0.02]"
            />
          </div>
          <Select value={selectedSeverity} onValueChange={setSelectedSeverity}>
            <SelectTrigger className="h-9 w-[130px] rounded-xl border-slate-200 text-xs font-bold dark:border-white/[0.08] dark:bg-white/[0.02]">
              <Layers className="h-3 w-3 text-slate-400" /> <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Severities</SelectItem>
              <SelectItem value="Critical">Critical</SelectItem>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="Low">Low</SelectItem>
              <SelectItem value="Observation">Observation</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedVerdict} onValueChange={setSelectedVerdict}>
            <SelectTrigger className="h-9 w-[130px] rounded-xl border-slate-200 text-xs font-black dark:border-white/[0.08] dark:bg-white/[0.02]">
              <SelectValue placeholder="Verdict" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Verdicts</SelectItem>
              <SelectItem value="FAIL">FAIL</SelectItem>
              <SelectItem value="PASS">PASS</SelectItem>
              <SelectItem value="EXCEPTION">Exception</SelectItem>
              <SelectItem value="INCONCLUSIVE">Inconclusive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedReviewStatus} onValueChange={setSelectedReviewStatus}>
            <SelectTrigger className="h-9 w-[130px] rounded-xl border-slate-200 text-xs font-black dark:border-white/[0.08] dark:bg-white/[0.02]">
              <FileCheck className="h-3 w-3 text-slate-400" /> <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Status</SelectItem>
              <SelectItem value="Reviewed">Reviewed</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedControl} onValueChange={setSelectedControl}>
            <SelectTrigger className="h-9 w-[150px] rounded-xl border-slate-200 text-xs font-black dark:border-white/[0.08] dark:bg-white/[0.02]">
              <Layers className="h-3 w-3 text-slate-400" /> <SelectValue placeholder="Control" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Controls</SelectItem>
              {controlOptions.map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="h-9 w-[150px] rounded-xl border-slate-200 text-xs font-black dark:border-white/[0.08] dark:bg-white/[0.02]">
              <ListFilter className="h-3 w-3 text-slate-400" /> <SelectValue placeholder="Group by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No grouping</SelectItem>
              <SelectItem value="control">Control family</SelectItem>
              <SelectItem value="severity">Severity</SelectItem>
              <SelectItem value="verdict">Verdict</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Two-pane body */}
        <div className="flex min-h-0 flex-1">
          {/* Left sidebar: Finding Groups / Severity / Verdict */}
          <aside className="hidden w-60 shrink-0 overflow-y-auto border-r border-slate-200 dark:border-white/[0.06] bg-slate-50/60 dark:bg-white/[0.01] p-4 lg:block">
            <SidebarGroup label="By Verdict">
              {VERDICT_LEVELS.map((v) => (
                <SidebarRow
                  key={v.key}
                  label={v.label}
                  count={v.count}
                  active={selectedVerdict === v.key}
                  onClick={() => setSelectedVerdict(v.key === selectedVerdict ? "ALL" : v.key)}
                  dot={v.dot}
                />
              ))}
            </SidebarGroup>

            <div className="my-4 border-t border-slate-200 dark:border-white/[0.06]" />


            <SidebarGroup label="By Severity">
              {SEVERITY_LEVELS.map((lv) => (
                <SidebarRow
                  key={lv.key}
                  label={lv.label}
                  count={lv.count}
                  active={selectedSeverity === lv.key}
                  onClick={() => setSelectedSeverity(lv.key === selectedSeverity ? "ALL" : lv.key)}
                  dot={lv.dot}
                />
              ))}
            </SidebarGroup>



          </aside>

          {/* Right pane: Findings list */}
          <section className="flex min-h-0 flex-1 flex-col">
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-white/[0.06] px-5 py-3">
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Findings</span>
              <span className="text-xs font-mono text-slate-400">{activeFindings.length} finding{activeFindings.length === 1 ? "" : "s"}</span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-5">
              {activeFindings.length > 0 ? (
                (() => {
                  let runningIndex = 0;
                  return groupedFindings.map((group, gi) => (
                    <div key={group.label || gi} className="space-y-3">
                      {group.label && (
                        <div className="flex items-center gap-2 px-1 pt-1">
                          <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">{group.label}</span>
                          <span className="rounded-md bg-slate-200/70 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-500 dark:bg-white/[0.06] dark:text-slate-400">{group.items.length}</span>
                          <div className="h-px flex-1 bg-slate-100 dark:bg-white/[0.04]" />
                        </div>
                      )}
                      {group.items.map((f) => {
                        const index = runningIndex++;
                        const vm = verdictMeta(f.verdict);
                        const VIcon = vm.Icon;
                        const controlRef = f.control_ref || f.control_id || "Unmapped";
                        const severity = f.final_severity || f.suggested_severity || f.severity || "—";
                        const evidenceLabel = evidenceOf(f);
                        const sevTone = severityTone(f);
                        const anomalyCount = getAnomalyCount(f);
                        const agents = Array.isArray(f.agents_involved) ? f.agents_involved.filter(Boolean) : [];
                        const refCount = getFindingCardCount(f);
                        const ratio = anomalyRatio(f);
                        return (
                          <Card
                            key={f.id}
                            data-testid={`finding-card-${controlRef}`}
                            className="finding-card-in overflow-hidden border-slate-200 shadow-sm transition-all hover:border-indigo-200 hover:shadow-md dark:border-white/[0.06]"
                            style={findingCardDelay(index)}
                          >
                            <div className={`h-1 w-full ${sevTone.bar}`} />
                            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/70 px-4 py-3 dark:border-white/[0.04] dark:bg-white/[0.02]">
                              <span className="font-mono text-xs font-bold text-slate-700 dark:text-slate-200">{controlRef}</span>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={`${sevTone.chip} px-2 py-0.5 text-[9px] font-black capitalize`}>
                                  {severity}
                                </Badge>
                                <div className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[8px] font-black ${vm.badgeCls}`}>
                                  <VIcon className="h-2.5 w-2.5" /> {vm.label}
                                </div>
                              </div>
                            </div>
                            <div className="p-4 space-y-3">
                              <p className="text-sm font-semibold leading-snug text-slate-900 dark:text-white line-clamp-2">
                                {sanitizeFindingTitle(f.title || f.finding_title || f.description || "Untitled signal")}
                              </p>
                              {f.rationale ? (
                                <div className="flex items-start gap-1.5">
                                  <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                                  <div className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-400 prose prose-sm dark:prose-invert max-w-none prose-headings:mt-2 prose-headings:mb-1 prose-p:my-0.5 prose-ul:my-0.5 prose-ul:list-disc prose-ul:pl-4 prose-li:my-0 prose-strong:text-slate-700 dark:prose-strong:text-slate-300">
                                    <ReactMarkdown>{f.rationale}</ReactMarkdown>
                                  </div>
                                </div>
                              ) : null}
                              <div className="flex flex-wrap gap-2">
                                {anomalyCount > 0 && (
                                  <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[9px] font-bold text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
                                    <AlertTriangle className="h-2.5 w-2.5" /> {anomalyCount} anomal{anomalyCount === 1 ? "y" : "ies"}
                                  </span>
                                )}
                                {agents.length > 0 && (
                                  <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[9px] font-bold text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300">
                                    <Bot className="h-2.5 w-2.5" /> {agents.length} agent{agents.length === 1 ? "" : "s"}
                                  </span>
                                )}
                                {refCount > 0 && (
                                  <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[9px] font-bold text-slate-600 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-slate-300">
                                    <FileText className="h-2.5 w-2.5" /> {refCount} evidence ref{refCount === 1 ? "" : "s"}
                                  </span>
                                )}
                                {ratio !== null && (
                                  <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[9px] font-bold text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                                    <AlertTriangle className="h-2.5 w-2.5" /> {ratio}% affected
                                  </span>
                                )}
                              </div>
                              {ratio !== null && (
                                <div className="flex items-center gap-2">
                                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-red-100 dark:bg-red-950/40">
                                    <div className="h-full rounded-full bg-red-500" style={{ width: `${ratio}%` }} />
                                  </div>
                                  <span className="font-mono text-[10px] font-bold text-red-600 dark:text-red-400">{ratio}%</span>
                                </div>
                              )}
                              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
                                <div className="flex items-center justify-between"><span className="text-slate-400 dark:text-slate-500">Control</span><span className="font-mono font-semibold text-slate-600 dark:text-slate-300">{controlRef}</span></div>
                                <div className="flex items-center justify-between"><span className="text-slate-400 dark:text-slate-500">Severity</span><span className="font-semibold capitalize text-slate-600 dark:text-slate-300">{severity}</span></div>
                                <div className="col-span-2 flex items-center justify-between"><span className="text-slate-400 dark:text-slate-500">Evidence</span><span className="font-mono font-semibold text-slate-600 dark:text-slate-300">{evidenceLabel}</span></div>
                              </dl>
                            </div>
                            <div className="flex items-center justify-end gap-2 border-t border-slate-100 p-3 dark:border-white/[0.04]">
                              <Button size="sm" className="h-8 rounded-lg bg-indigo-600 text-xs font-semibold text-white hover:bg-indigo-500" onClick={() => { setPreviewDoc(null); setPreviewPage(null); setPreviewSearch(""); setSelectedFinding(f); }}>
                                Detailed View <ArrowRight className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  ));
                })()
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Search className="h-10 w-10 text-slate-300 dark:text-white/10 mb-3" />
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">No findings match the current filters</h3>
                  <p className="mt-1 text-xs text-slate-500">Try adjusting the search, verdict, or severity filters.</p>
                  <Button variant="link" className="mt-3 text-xs font-bold text-indigo-600 dark:text-indigo-400" onClick={() => { setSelectedVerdict("ALL"); setSelectedSeverity("ALL"); setSelectedReviewStatus("ALL"); setSelectedGroup("all"); setSelectedControl("ALL"); setSearchTerm(""); }}>
                    Clear all filters
                  </Button>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 dark:border-white/[0.06] bg-slate-50/70 dark:bg-white/[0.01] px-5 py-3">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
            <span className="font-bold text-indigo-600">{needsReviewCount}</span> finding{needsReviewCount === 1 ? "" : "s"} need review
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-9 rounded-lg text-xs font-semibold" onClick={handleReviewUnresolved} disabled={needsReviewCount === 0}>
              Review Unresolved
            </Button>
            <Button size="sm" className="h-9 gap-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-700 text-xs font-bold text-white hover:from-emerald-400 hover:to-emerald-600" onClick={handleContinue} disabled={!canAdvanceToClassification}>
              Continue <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* (dialogs follow) */}

      {/* Slide-out Detail Panel (Prototype Style) */}
      <Sheet open={!!selectedFinding} onOpenChange={(open) => !open && closeSheet()}>
        <SheetContent side="right" className={`${previewDoc ? "!max-w-[1280px]" : "!max-w-[550px]"} !w-full p-0 border-l border-slate-200 dark:border-white/[0.08] bg-white dark:bg-[#0b0d11] shadow-2xl transition-all [&>button]:hidden`}>
          {selectedFinding && (
            <div className="flex h-full w-full text-slate-900 dark:text-slate-100 overflow-hidden">
              <ResizablePanelGroup direction="horizontal" className="h-full w-full">
                <ResizablePanel defaultSize={previewDoc ? 55 : 100} minSize={35} className="min-w-0">
                  <div className="flex flex-col h-full min-w-0">
                    {/* Header Section: Matches Arena Aesthetic */}
                    <div className="p-8 pb-6 space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400">
                            {selectedFinding.control_ref || "CONTROL"}
                          </p>
                          <h3 className="text-xl font-bold text-slate-900 dark:text-white leading-tight">
                            {selectedFinding.title || selectedFinding.description}
                          </h3>
                        </div>
                        <button onClick={closeSheet} className="text-slate-400 hover:text-white transition-colors">
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>

                      <div className="flex items-center gap-2">
                        <Badge className={`px-3 py-1 rounded-full text-[9px] font-black border-2 ${verdictMeta(selectedFinding.verdict).badgeCls}`}>
                          {selectedFinding.verdict}
                        </Badge>
                      </div>
                    </div>

                    {/* Content Scroll Area */}
                    <div className="flex-1 overflow-y-auto px-8 pb-8 space-y-8 no-scrollbar">
                      {/* Control Description */}
                      {selectedFinding.description && (
                        <div className="space-y-3">
                          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Control Description</p>
                          <div className="p-5 bg-slate-50 dark:bg-white/[0.01] border border-slate-200 dark:border-white/[0.06] rounded-xl">
                            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                              {selectedFinding.description}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Evidence Narrative: High-Impact Box */}
                      <div className="space-y-3">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Evidence Narrative</p>
                        <div className="p-6 bg-emerald-50 dark:bg-emerald-500/[0.02] border border-emerald-200/50 dark:border-emerald-500/20 rounded-xl">
                          <div className="text-sm text-emerald-900 dark:text-emerald-50/80 leading-relaxed font-medium prose prose-sm dark:prose-invert max-w-none prose-headings:mt-3 prose-headings:mb-1.5 prose-p:my-1 prose-ul:my-1 prose-ul:list-disc prose-ul:pl-4 prose-li:my-0 prose-strong:text-emerald-900 dark:prose-strong:text-emerald-50/90 prose-strong:font-bold">
                            <ReactMarkdown>{selectedFinding.evidence_narrative || "The evidence identifies specific patterns of concern that align with the cited control requirements."}</ReactMarkdown>
                          </div>
                        </div>
                      </div>

                      {/* Rationale: why this verdict/severity was reached */}
                      {selectedFinding?.rationale && (
                        <div className="space-y-3">
                          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Rationale</p>
                          <div className="p-5 bg-amber-50 dark:bg-amber-500/[0.04] border border-amber-200/60 dark:border-amber-500/20 rounded-xl">
                            <div className="flex items-start gap-2.5">
                              <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                              <div className="text-sm text-amber-900 dark:text-amber-100/80 leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-headings:mt-3 prose-headings:mb-1.5 prose-p:my-1 prose-ul:my-1 prose-ul:list-disc prose-ul:pl-4 prose-li:my-0 prose-strong:text-amber-900 dark:prose-strong:text-amber-50/90 prose-strong:font-bold">
                                <ReactMarkdown>{selectedFinding.rationale}</ReactMarkdown>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Key Evidence */}
                      {keyEvidence.length > 0 && (
                        <div className="space-y-3">
                          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Key Evidence</p>
                          <div className="space-y-2">
                            {keyEvidence.map((ev) => (
                              <KeyEvidenceRow key={ev.id} ev={ev} onPreview={handleEvidencePreview} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Plan */}
                      {actionFields.length > 0 && (
                        <div className="space-y-3">
                          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Action Plan</p>
                          <div className="space-y-2">
                            {actionFields.map((af) => (
                              <div key={af.label} className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-white/[0.06] dark:bg-white/[0.01]">
                                <div className="flex items-center gap-1.5">
                                  <af.Icon className="h-3 w-3 text-indigo-500" />
                                  <p className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">{af.label}</p>
                                </div>
                                <p className="mt-1.5 text-xs leading-relaxed whitespace-pre-wrap text-slate-700 dark:text-slate-300">{af.value}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer Action Bar: Redesigned for Arena Aesthetic */}
                    <div className="p-8 border-t border-slate-100 dark:border-white/[0.08] bg-slate-50 dark:bg-white/[0.01]">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateFindingReview(selectedFinding.id, { status: "accepted" })}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black uppercase transition-all ${(findingReviews[selectedFinding.id]?.status || selectedFinding.review_status) === 'accepted'
                                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                                : 'bg-white dark:bg-white/[0.03] text-slate-500 border border-slate-200 dark:border-white/[0.06] hover:bg-emerald-500/10 hover:text-emerald-500'
                              }`}
                          >
                            <CheckCircle2 className="h-4 w-4" /> Accept
                          </button>
                          <button
                            onClick={() => updateFindingReview(selectedFinding.id, { status: "rejected" })}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black uppercase transition-all ${(findingReviews[selectedFinding.id]?.status || selectedFinding.review_status) === 'rejected'
                                ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/20'
                                : 'bg-white dark:bg-white/[0.03] text-slate-500 border border-slate-200 dark:border-white/[0.06] hover:bg-rose-500/10 hover:text-rose-500'
                              }`}
                          >
                            <XCircle className="h-4 w-4" /> Reject
                          </button>
                        </div>
                        <Button variant="ghost" className="h-11 rounded-xl text-[10px] font-bold text-slate-400 hover:text-slate-900 dark:hover:text-white" onClick={closeSheet}>
                          Dismiss Panel
                        </Button>
                      </div>
                    </div>
                  </div>
                </ResizablePanel>

                {previewDoc && (
                  <>
                    <ResizableHandle withHandle className="bg-slate-200 dark:bg-white/[0.08]" />
                    <ResizablePanel defaultSize={45} minSize={20} maxSize={60} className="min-w-0">
                      <div className="flex flex-col h-full min-w-0">
                        <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 bg-slate-50 dark:border-white/[0.08] dark:bg-white/[0.01]">
                          <div className="flex min-w-0 items-center gap-2">
                            <FileText className="h-4 w-4 shrink-0 text-indigo-500" />
                            <span className="truncate text-xs font-bold text-slate-700 dark:text-slate-200">{previewDoc.filename}</span>
                            {previewPage ? (
                              <Badge variant="outline" className="shrink-0 font-mono text-[9px] font-bold text-indigo-700 dark:text-indigo-300">p.{previewPage}</Badge>
                            ) : null}
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            {paneUrl && (
                              <button
                                type="button"
                                title="Open in new tab"
                                onClick={() => window.open(paneUrl, "_blank", "noopener,noreferrer")}
                                className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-500/10 transition-colors"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              type="button"
                              title="Close preview"
                              onClick={() => { setPreviewDoc(null); setPreviewPage(null); setPreviewSearch(""); setPaneUrl(null); }}
                              className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-500/10 transition-colors"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        <div className="flex-1 min-h-0 bg-slate-100 dark:bg-black/40">
                          <EvidencePdfFrame auditRunId={auditRun?.id} item={previewDoc} page={previewPage} search={previewSearch} onUrl={setPaneUrl} />
                        </div>
                      </div>
                    </ResizablePanel>
                  </>
                )}
              </ResizablePanelGroup>
            </div>
          )}
        </SheetContent>
      </Sheet>




      {/* Traceability View */}
      <Dialog open={!!traceView} onOpenChange={() => setTraceView(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Traceability View</DialogTitle></DialogHeader>
          {traceView && (
            <div className="space-y-3 text-sm">
              <div className="p-3 border rounded-md">
                <p className="label-xs text-muted-foreground mb-1">Finding</p>
                <p>{traceView.finding_description}</p>
              </div>
              <div className="p-3 border rounded-md">
                <p className="label-xs text-muted-foreground mb-1">Evidence Summary</p>
                <pre className="whitespace-pre-wrap font-sans text-xs bg-muted/30 p-2 rounded">{sanitizeEvidenceSummary(traceView.evidence_summary)}</pre>
              </div>
              {!!getEvidenceReferences(traceView).length && (
                <div className="p-3 border rounded-md">
                  <p className="label-xs text-muted-foreground mb-2">Evidence References</p>
                  <ExpandableBadgeList items={getEvidenceReferences(traceView)} limit={12} />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Details View */}
      {/* <Dialog open={!!detailsView} onOpenChange={() => setDetailsView(null)}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-hidden dark:border-emerald-400/20 dark:bg-slate-950 dark:text-slate-100">
          <DialogHeader><DialogTitle>Finding Detail Snapshot</DialogTitle></DialogHeader>
          {detailsView && (
            <div className="mt-2 max-h-[76vh] overflow-y-auto pr-2 text-sm">
              <div className="space-y-4">
                <div className="rounded-2xl border border-border/80 bg-muted/20 p-4 dark:border-emerald-400/20 dark:bg-slate-900/70">
                  <div className="flex flex-wrap items-center gap-2">
                    {(detailsView.control_id || detailsView.category) && (
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {detailsView.control_id || detailsView.category}
                      </Badge>
                    )}
                    {(detailsView.final_severity || detailsView.suggested_severity || detailsView.severity) && (
                      <Badge
                        variant="outline"
                        className={`text-xs border ${sevClass(detailsView.final_severity || detailsView.suggested_severity || detailsView.severity)}`}
                      >
                        {detailsView.final_severity || detailsView.suggested_severity || detailsView.severity}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-3 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Finding Context</p>
                  <p className="mt-2 text-base font-semibold leading-7">
                    {sanitizeFindingTitle(detailsView.finding_title) || (detailsView.finding_description && `${detailsView.finding_description.substring(0, 100)}...`) || "Untitled finding"}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {detailsView.finding_description || "No finding description captured."}
                  </p>
                </div>

                <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4 dark:border-cyan-300/20 dark:bg-cyan-300/10">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-blue-600 dark:text-cyan-300" />
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700 dark:text-cyan-300">Auditor Explainability</p>
                    </div>
                    <Badge variant="outline" className="bg-white/80 text-xs dark:bg-slate-900/80 dark:text-slate-100">
                      {getAnomalyCount(detailsView)} anomalies
                    </Badge>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div className="rounded-xl bg-white/75 p-3 dark:bg-slate-900/75">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Why this was flagged</p>
                      <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-100">{auditorExplanation(detailsView).whyFlagged}</p>
                    </div>
                    <div className="rounded-xl bg-white/75 p-3 dark:bg-slate-900/75">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Evidence basis</p>
                      <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-100">{auditorExplanation(detailsView).evidenceBasis}</p>
                    </div>
                    <div className="rounded-xl bg-white/75 p-3 dark:bg-slate-900/75">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Likely cause</p>
                      <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-100">{auditorExplanation(detailsView).likelyCause}</p>
                    </div>
                    <div className="rounded-xl bg-white/75 p-3 dark:bg-slate-900/75">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Auditor next step</p>
                      <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-100">{auditorExplanation(detailsView).nextStep}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="rounded-2xl border border-border/80 p-4 dark:border-emerald-400/20 dark:bg-slate-900/60">
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Business Impact</p>
                    <p className="mt-2 whitespace-pre-wrap leading-6">{businessImpactValue(detailsView)}</p>
                  </div>
                  <div className="rounded-2xl border border-border/80 p-4 dark:border-emerald-400/20 dark:bg-slate-900/60">
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Root Cause</p>
                    <p className="mt-2 whitespace-pre-wrap leading-6">{rootCauseValue(detailsView)}</p>
                  </div>
                  <div className="rounded-2xl border border-border/80 p-4 dark:border-emerald-400/20 dark:bg-slate-900/60">
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Remediation Plan</p>
                    <p className="mt-2 whitespace-pre-wrap leading-6">{remediationPlanValue(detailsView)}</p>
                  </div>
                  <div className="rounded-2xl border border-border/80 p-4 dark:border-emerald-400/20 dark:bg-slate-900/60">
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Management Response</p>
                    <p className="mt-2 whitespace-pre-wrap leading-6">{managementResponseValue(detailsView)}</p>
                  </div>
                </div>

                {!!(detailsView.evidence_summary || getEvidenceReferences(detailsView).length) && (
                  <div className="rounded-2xl border border-border/80 p-4 dark:border-emerald-400/20 dark:bg-slate-900/60">
                    <Label className="block text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Evidence Summary</Label>
                    <p className="mt-2 text-sm leading-6">
                      {sanitizeEvidenceSummary(detailsView.evidence_summary) || "Evidence references captured without a written summary."}
                    </p>
                    {!!getEvidenceReferences(detailsView).length && (
                      <ExpandableBadgeList items={getEvidenceReferences(detailsView)} limit={20} className="mt-3" />
                    )}
                  </div>
                )}

                {!!detailsView.affected_references?.length && (
                  <div>
                    <Label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Affected Policies / References</Label>
                    <div className="flex flex-wrap gap-2 rounded-2xl border border-border/80 bg-card p-4 dark:border-emerald-400/20 dark:bg-slate-900/60">
                      {detailsView.affected_references.map((item, i) => (
                        <Badge key={i} variant="outline" className="font-mono text-xs">{item}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Identified Anomalies ({getAnomalyEntries(detailsView).length})
                  </Label>
                  {getAnomalyEntries(detailsView).length > 0 ? (
                    <div className="grid gap-3 lg:grid-cols-2">
                      {getAnomalyEntries(detailsView).slice(0, MODAL_ANOMALY_PREVIEW_LIMIT).map((item) => (
                        <div key={item.id} className="rounded-2xl border border-border/80 bg-card p-4 dark:border-emerald-400/20 dark:bg-slate-900/60">
                          <p className="text-sm font-semibold">{item.title}</p>
                          <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">{item.preview}</p>
                          <div className="mt-3 whitespace-pre-wrap rounded-xl bg-muted/40 p-3 text-xs leading-5 text-slate-700 dark:bg-slate-950/70 dark:text-slate-200">
                            {formatAnomalySummary(item)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed p-6 text-center">
                      <p className="text-sm text-muted-foreground">No specific anomalous items were extracted for this finding.</p>
                    </div>
                  )}
                  {getAnomalyEntries(detailsView).length > MODAL_ANOMALY_PREVIEW_LIMIT && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Showing the first {MODAL_ANOMALY_PREVIEW_LIMIT} anomalies to keep the modal readable.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog> */}

      {/* Edit Dialog */}
      <Dialog open={!!editFinding} onOpenChange={() => setEditFinding(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Finding</DialogTitle></DialogHeader>
          {editFinding && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Control ID</Label><Input value={editFinding.control_id || editFinding.control_ref || ""} onChange={e => setEditFinding(p => ({ ...p, control_id: e.target.value }))} className="mt-1" /></div>
                <div><Label className="text-xs">Severity Override</Label>
                  <Select value={editFinding.final_severity || editFinding.suggested_severity || "Medium"} onValueChange={v => setEditFinding(p => ({ ...p, final_severity: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Critical">Critical</SelectItem><SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem><SelectItem value="Observation">Observation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">Title</Label><Input value={editFinding.finding_title || ""} onChange={e => setEditFinding(p => ({ ...p, finding_title: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Description</Label><Textarea value={editFinding.finding_description || ""} onChange={e => setEditFinding(p => ({ ...p, finding_description: e.target.value }))} className="mt-1" rows={4} /></div>
              <div><Label className="text-xs">Evidence Summary</Label><Textarea value={editFinding.evidence_summary || ""} onChange={e => setEditFinding(p => ({ ...p, evidence_summary: e.target.value }))} className="mt-1" rows={2} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Business Impact</Label><Textarea value={editFinding.business_impact || ""} onChange={e => setEditFinding(p => ({ ...p, business_impact: e.target.value }))} className="mt-1" rows={2} /></div>
                <div><Label className="text-xs">Root Cause</Label><Textarea value={editFinding.root_cause || ""} onChange={e => setEditFinding(p => ({ ...p, root_cause: e.target.value }))} className="mt-1" rows={2} /></div>
              </div>
              <div><Label className="text-xs">Remediation Plan</Label><Textarea value={editFinding.remediation_plan || ""} onChange={e => setEditFinding(p => ({ ...p, remediation_plan: e.target.value }))} className="mt-1" rows={2} /></div>
              <div><Label className="text-xs">Owner / Management Response</Label><Textarea value={editFinding.owner_response || editFinding.management_response || ""} onChange={e => setEditFinding(p => ({ ...p, owner_response: e.target.value }))} className="mt-1" rows={2} /></div>
              <div><Label className="text-xs">Affected References (comma-separated)</Label><Input value={editFinding.affected_references_text ?? (Array.isArray(editFinding.affected_references) ? editFinding.affected_references.join(", ") : "")} onChange={e => setEditFinding(p => ({ ...p, affected_references_text: e.target.value }))} className="mt-1" placeholder="e.g. INV-001, POL-123" /></div>
              <Button onClick={handleEdit} className="w-full">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Manual Finding</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Control ID *</Label><Input value={newFinding.control_id} onChange={e => setNewFinding(p => ({ ...p, control_id: e.target.value }))} className="mt-1" placeholder="e.g. CTRL-001" /></div>
              <div><Label className="text-xs">Severity</Label>
                <Select value={newFinding.suggested_severity} onValueChange={v => setNewFinding(p => ({ ...p, suggested_severity: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Critical">Critical</SelectItem><SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem><SelectItem value="Observation">Observation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Category</Label>
                <Select value={newFinding.category} onValueChange={v => setNewFinding(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operational">Operational</SelectItem><SelectItem value="compliance">Compliance</SelectItem><SelectItem value="financial">Financial</SelectItem><SelectItem value="it">IT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Affected References (comma-separated)</Label><Input value={newFinding.affected_references_text} onChange={e => setNewFinding(p => ({ ...p, affected_references_text: e.target.value }))} className="mt-1" placeholder="e.g. INV-001, POL-123" /></div>
            </div>
            <div><Label className="text-xs">Title *</Label><Input value={newFinding.finding_title} onChange={e => setNewFinding(p => ({ ...p, finding_title: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={newFinding.finding_description} onChange={e => setNewFinding(p => ({ ...p, finding_description: e.target.value }))} className="mt-1" rows={3} /></div>
            <div><Label className="text-xs">Evidence Summary</Label><Textarea value={newFinding.evidence_summary} onChange={e => setNewFinding(p => ({ ...p, evidence_summary: e.target.value }))} className="mt-1" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Business Impact</Label><Textarea value={newFinding.business_impact} onChange={e => setNewFinding(p => ({ ...p, business_impact: e.target.value }))} className="mt-1" rows={2} /></div>
              <div><Label className="text-xs">Root Cause</Label><Textarea value={newFinding.root_cause} onChange={e => setNewFinding(p => ({ ...p, root_cause: e.target.value }))} className="mt-1" rows={2} /></div>
            </div>
            <div><Label className="text-xs">Remediation Plan</Label><Textarea value={newFinding.remediation_plan} onChange={e => setNewFinding(p => ({ ...p, remediation_plan: e.target.value }))} className="mt-1" rows={2} /></div>
            <div><Label className="text-xs">Owner / Management Response</Label><Textarea value={newFinding.owner_response} onChange={e => setNewFinding(p => ({ ...p, owner_response: e.target.value }))} className="mt-1" rows={2} /></div>
            <Button onClick={handleAdd} className="w-full">Add Finding</Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
